import { resultatDesVentes, signeDansLeChiffre } from '../schemas/sales.js';
/** Nombre de tranches affichées pour les jours et les mois. */
export const TRANCHES_PAR_PAS = { jour: 30, mois: 12 };
export function cleDeTranche(date, pas) {
    return pas === 'jour' ? date.slice(0, 10) : pas === 'mois' ? date.slice(0, 7) : date.slice(0, 4);
}
const deux = (n) => String(n).padStart(2, '0');
const civile = (d) => `${d.getUTCFullYear()}-${deux(d.getUTCMonth() + 1)}-${deux(d.getUTCDate())}`;
/**
 * Les tranches d'un graphe, de la plus ancienne à celle d'AUJOURD'HUI incluse.
 *
 * ⚠️ La tranche en cours s'arrête à aujourd'hui, pas à la fin de sa période :
 * c'est la borne de la carte, et une dépense saisie par avance pour le 30 ne
 * doit pas gonfler la colonne du 10.
 *
 * @param premiereAnnee pour le pas annuel : la première année d'activité. Les
 *        années d'AVANT n'apparaissent pas — la boutique n'existait pas, un zéro
 *        y serait une fausse information.
 */
export function tranches(pas, aujourdhui, premiereAnnee) {
    const [a, m, j] = aujourdhui.split('-').map(Number);
    if (pas === 'jour') {
        return Array.from({ length: TRANCHES_PAR_PAS.jour }, (_, i) => {
            const d = civile(new Date(Date.UTC(a, m - 1, j - (TRANCHES_PAR_PAS.jour - 1 - i))));
            return { cle: d, du: d, au: d };
        });
    }
    if (pas === 'mois') {
        return Array.from({ length: TRANCHES_PAR_PAS.mois }, (_, i) => {
            const debut = new Date(Date.UTC(a, m - 1 - (TRANCHES_PAR_PAS.mois - 1 - i), 1));
            // Le jour 0 du mois suivant est le dernier jour de celui-ci.
            const fin = civile(new Date(Date.UTC(debut.getUTCFullYear(), debut.getUTCMonth() + 1, 0)));
            const du = civile(debut);
            return { cle: du.slice(0, 7), du, au: fin < aujourdhui ? fin : aujourdhui };
        });
    }
    const depuis = Math.min(premiereAnnee ?? a, a);
    return Array.from({ length: a - depuis + 1 }, (_, i) => {
        const annee = depuis + i;
        const fin = `${annee}-12-31`;
        return { cle: String(annee), du: `${annee}-01-01`, au: fin < aujourdhui ? fin : aujourdhui };
    });
}
/**
 * Les ventes, regroupées par tranche.
 *
 * ⭐ Les règles sont CELLES DES CARTES, réutilisées et non recopiées : ce qui
 * compte dans le chiffre (`signeDansLeChiffre`) et ce qu'ont coûté les
 * articles vendus (`resultatDesVentes`). Une règle recopiée finit par
 * diverger, et le graphe contredirait la carte qui l'ouvre.
 */
export function serieDesVentes(ventes, articles, pas, decoupage) {
    const debut = decoupage[0]?.du ?? '';
    const fin = decoupage.at(-1)?.au ?? '';
    const parCle = new Map();
    for (const v of ventes) {
        if (signeDansLeChiffre(v) === 0 || v.issueDate < debut || v.issueDate > fin)
            continue;
        const cle = cleDeTranche(v.issueDate, pas);
        const groupe = parCle.get(cle);
        if (groupe)
            groupe.push(v);
        else
            parCle.set(cle, [v]);
    }
    return decoupage.map(({ cle }) => {
        const groupe = parCle.get(cle) ?? [];
        const ca = groupe.reduce((s, v) => s + signeDansLeChiffre(v) * v.totalAmount, 0);
        const passages = groupe.filter((v) => signeDansLeChiffre(v) === 1).length;
        return {
            cle,
            ca,
            ventes: passages,
            panierMoyen: passages > 0 ? Math.round(ca / passages) : null,
            ...resultatDesVentes(groupe, articles),
        };
    });
}
/** Compte des dates CIVILES par tranche — les nouveaux clients, par exemple. */
export function compterParTranche(dates, pas, decoupage) {
    const debut = decoupage[0]?.du ?? '';
    const fin = decoupage.at(-1)?.au ?? '';
    const comptes = new Map();
    for (const d of dates) {
        if (d < debut || d > fin)
            continue;
        const cle = cleDeTranche(d, pas);
        comptes.set(cle, (comptes.get(cle) ?? 0) + 1);
    }
    return decoupage.map(({ cle }) => comptes.get(cle) ?? 0);
}
//# sourceMappingURL=series.js.map